/*
 * Product description tabs and the built-in review form.
 *
 * <kv-pdp-tabs>     switches between the panels of the description box.
 *                   Follows the ARIA tabs pattern: click, arrow keys, Home, End.
 *                   Opens the reviews panel and scrolls to it when a link to
 *                   #reviews (or an element with data-kv-reviews-open) is
 *                   clicked anywhere on the page, when the address ends in
 *                   #reviews on load or later, when the document receives a
 *                   'kv-reviews:open' event, or when the review form has just
 *                   been sent.
 * <kv-review-form>  asks for a star rating before sending, says the review is
 *                   on its way, sends picked pictures and clips to the address
 *                   the settings name and keeps their links with the review,
 *                   and can remember the reviewer's name and email in this
 *                   browser.
 * Star rows (kv-rating-stars) lose their #reviews link on a page that has no
 * Reviews tab to open.
 */

if (!customElements.get('kv-pdp-tabs')) {
  customElements.define(
    'kv-pdp-tabs',
    class KvPdpTabs extends HTMLElement {
      connectedCallback() {
        // With a single tab and its name hidden there is no tab row, but the
        // reviews panel can still be scrolled to.
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.tabs.forEach((tab, index) => {
          tab.addEventListener('click', () => this.select(index, false));
          tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
        });

        this.onHashChange = this.onHashChange.bind(this);
        this.onOpenRequest = () => {
          if (this.ownsReviews()) this.open('reviews', true);
        };
        this.onDocumentClick = this.onDocumentClick.bind(this);
        window.addEventListener('hashchange', this.onHashChange);
        document.addEventListener('kv-reviews:open', this.onOpenRequest);
        document.addEventListener('click', this.onDocumentClick);

        if (this.querySelector('[data-posted]')) {
          this.open('reviews', true);
        } else {
          this.onHashChange();
        }
      }

      disconnectedCallback() {
        window.removeEventListener('hashchange', this.onHashChange);
        document.removeEventListener('kv-reviews:open', this.onOpenRequest);
        document.removeEventListener('click', this.onDocumentClick);
      }

      panel(name) {
        return this.querySelector(`[id^="KvPdpPanel-${name}-"]`);
      }

      // Page-wide requests (links, the event, the address) are answered by the
      // box that #reviews points to, so a second description box on the page
      // never competes with the first.
      ownsReviews() {
        return document.getElementById('reviews') === this && Boolean(this.panel('reviews'));
      }

      // A plain click on a #reviews link would only jump, and a second click
      // would do nothing as the address already ends in #reviews; the tab is
      // opened here instead, and the address still ends in #reviews. The
      // keyboard position moves to the Reviews tab, as a jump would move it.
      onDocumentClick(event) {
        if (event.defaultPrevented || event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const trigger = event.target.closest('a[href="#reviews"], [data-kv-reviews-open]');
        if (!trigger || this.contains(trigger) || !this.ownsReviews()) return;
        event.preventDefault();
        if (window.location.hash !== '#reviews') window.history.pushState(null, '', '#reviews');
        this.open('reviews', true);
        this.focusReviews();
      }

      focusReviews() {
        if (this.querySelector('[data-posted]')) return;
        const tab = this.tabs.find((item) => item.dataset.tab === 'reviews');
        const target = tab || this.panel('reviews');
        if (!tab) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      }

      select(index, moveFocus) {
        this.tabs.forEach((tab, i) => {
          const selected = i === index;
          const panel = document.getElementById(tab.getAttribute('aria-controls'));
          tab.setAttribute('aria-selected', String(selected));
          tab.tabIndex = selected ? 0 : -1;
          tab.classList.toggle('kv-pdp-desc__tab--active', selected);
          if (panel) panel.hidden = !selected;
        });
        if (moveFocus) this.tabs[index].focus();
      }

      open(name, scroll) {
        if (!this.panel(name)) return;
        const index = this.tabs.findIndex((tab) => tab.dataset.tab === name);
        if (index >= 0) this.select(index, false);
        if (scroll) {
          const message = this.querySelector('[data-posted]');
          this.scrollToTop(message || this);
          if (message) message.focus({ preventScroll: true });
        }
      }

      // Brings the target to the top of the window, just below whatever is
      // pinned over the top edge (a sticky header), plus the element's own
      // scroll-margin-top. A header that only sticks once the page has
      // scrolled is not there when the scroll starts, so once the page stops
      // moving the position is checked and corrected - but only when the page
      // is still where it was sent, so a shopper who has scrolled away or
      // gone back in the meantime is left alone.
      scrollToTop(target) {
        const margin = parseFloat(getComputedStyle(target).scrollMarginTop) || 0;
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        const maxTop = () => document.documentElement.scrollHeight - window.innerHeight;
        let sentTo = 0;
        const scroll = () => {
          const top = target.getBoundingClientRect().top + window.scrollY - this.coveredTop(target) - margin;
          sentTo = Math.round(Math.min(Math.max(0, top), maxTop()));
          window.scrollTo({ top: sentTo, behavior });
        };
        scroll();
        this.whenScrollStops(() => {
          if (!target.isConnected || Math.abs(window.scrollY - sentTo) > 2) return;
          const offset = target.getBoundingClientRect().top - this.coveredTop(target) - margin;
          if (Math.abs(offset) > 1) scroll();
        });
      }

      // Bottom edge of anything fixed or sticky that covers the top of the
      // window, other than the target itself; 0 when nothing does.
      coveredTop(target) {
        let bottom = 0;
        document.elementsFromPoint(window.innerWidth / 2, 1).forEach((element) => {
          if (target.contains(element)) return;
          for (let node = element; node && node !== document.body; node = node.parentElement) {
            const position = getComputedStyle(node).position;
            if (position === 'fixed' || position === 'sticky') {
              const rect = node.getBoundingClientRect();
              if (rect.top <= 1 && rect.height < window.innerHeight / 2) bottom = Math.max(bottom, rect.bottom);
              break;
            }
          }
        });
        return bottom;
      }

      // Calls back once the page has kept the same scroll position for two
      // checks in a row, or after two seconds at the latest.
      whenScrollStops(callback) {
        let last = window.scrollY;
        let still = 0;
        let checks = 0;
        const check = () => {
          checks += 1;
          still = window.scrollY === last ? still + 1 : 0;
          last = window.scrollY;
          if (still >= 2 || checks >= 20) callback();
          else window.setTimeout(check, 100);
        };
        window.setTimeout(check, 100);
      }

      onHashChange() {
        if (window.location.hash === '#reviews' && this.ownsReviews()) this.open('reviews', true);
      }

      onKeydown(event, index) {
        const last = this.tabs.length - 1;
        let next;
        switch (event.key) {
          case 'ArrowRight':
            next = index === last ? 0 : index + 1;
            break;
          case 'ArrowLeft':
            next = index === 0 ? last : index - 1;
            break;
          case 'Home':
            next = 0;
            break;
          case 'End':
            next = last;
            break;
          default:
            return;
        }
        event.preventDefault();
        this.select(next, true);
      }
    }
  );
}

if (!customElements.get('kv-review-form')) {
  customElements.define(
    'kv-review-form',
    class KvReviewForm extends HTMLElement {
      static get storageKey() {
        return 'kv-reviews-author';
      }

      connectedCallback() {
        this.setUpUpload();

        this.form = this.querySelector('form');
        if (!this.form) return;

        this.ratingInputs = Array.from(this.querySelectorAll('[data-rating-input]'));
        this.ratingMessage = this.querySelector('[data-rating-message]');
        this.remember = this.querySelector('[data-remember]');
        this.nameInput = this.querySelector('[data-review-name]');
        this.emailInput = this.querySelector('[data-review-email]');
        this.sending = this.querySelector('[data-sending]');

        this.restoreAuthor();

        // A radio group that is still empty fires "invalid" before the form is
        // sent; show the message next to the stars instead of the browser bubble.
        // Focus moves to the stars only when every other field is filled in,
        // otherwise the browser points at the first empty field as usual.
        this.ratingInputs.forEach((input) => {
          input.addEventListener('invalid', (event) => {
            event.preventDefault();
            this.showRatingMessage(true);
            if (!this.form.querySelector(':invalid:not([data-rating-input])')) {
              this.ratingInputs[0].focus();
            }
          });
          input.addEventListener('change', () => this.showRatingMessage(false));
        });

        this.form.addEventListener('submit', (event) => {
          if (!this.ratingInputs.some((input) => input.checked)) {
            event.preventDefault();
            this.showRatingMessage(true);
            this.ratingInputs[0].focus();
            return;
          }
          // Pictures still on their way would be lost when the page moves on,
          // so the review waits and goes by itself once they are in.
          if (this.uploadPending) {
            event.preventDefault();
            this.sendWhenUploaded = true;
            if (this.sending) this.sending.hidden = false;
            return;
          }
          this.saveAuthor();
          if (this.sending) this.sending.hidden = false;
        });

        // If sending was held up (for example a spam check the shopper closed)
        // and the shopper edits the form again, the "being submitted" line would
        // be wrong, so it goes until the next attempt.
        if (this.sending) {
          const reset = () => {
            this.sending.hidden = true;
          };
          this.form.addEventListener('input', reset);
          this.form.addEventListener('change', reset);
        }
      }

      // Pictures and clips cannot travel in the store's own form, so each one
      // is sent to the address the settings name and only the links it
      // answers with go with the review. The row is only in the page when
      // that address is set.
      setUpUpload() {
        const upload = this.querySelector('[data-review-upload]');
        const input = upload && upload.querySelector('[data-upload-input]');
        const note = upload && upload.querySelector('[data-upload-note]');
        const links = upload && upload.querySelector('[data-upload-links]');
        if (!upload || !input || !note || !links) return;

        // The row waits out of sight until this runs, so a browser with
        // scripts turned off never offers a file box that could not send
        // anything.
        upload.hidden = false;

        const say = (text) => {
          note.textContent = text || '';
          note.hidden = !text;
        };

        input.addEventListener('change', async () => {
          const max = parseInt(upload.dataset.maxFiles, 10) || 5;
          const files = Array.from(input.files).slice(0, max);
          if (files.length === 0) return;
          this.uploadPending = true;
          say(upload.dataset.sending);
          try {
            const urls = [];
            for (const file of files) {
              const body = new FormData();
              body.append('file', file);
              const response = await fetch(upload.dataset.endpoint, { method: 'POST', body });
              if (!response.ok) throw new Error(String(response.status));
              // The service may answer with the link as plain text, or as
              // JSON holding url, link or location.
              const answer = await response.text();
              let url = answer.trim();
              try {
                const parsed = JSON.parse(answer);
                url = parsed.url || parsed.link || parsed.location || '';
              } catch (error) {
                // Not JSON; the plain text is the link.
              }
              if (!url) throw new Error('no link');
              urls.push(url);
            }
            links.value = urls.join('\n');
            say(files.map((file) => file.name).join(', '));
            this.uploadPending = false;
            if (this.sendWhenUploaded) {
              this.sendWhenUploaded = false;
              this.form.requestSubmit();
            }
          } catch (error) {
            links.value = '';
            say(upload.dataset.failed);
            // A review held back for these files is not sent on its own: the
            // shopper reads why and decides whether to send it as it is.
            this.uploadPending = false;
            this.sendWhenUploaded = false;
          }
        });
      }

      showRatingMessage(show) {
        if (!this.ratingMessage) return;
        this.ratingMessage.textContent = show ? this.dataset.ratingError || '' : '';
        this.ratingMessage.hidden = !show || !this.dataset.ratingError;
      }

      restoreAuthor() {
        if (!this.remember || !this.nameInput || !this.emailInput) return;
        let saved = null;
        try {
          saved = JSON.parse(window.localStorage.getItem(KvReviewForm.storageKey));
        } catch (error) {
          saved = null;
        }
        if (!saved) return;
        if (!this.nameInput.value) this.nameInput.value = saved.name || '';
        if (!this.emailInput.value) this.emailInput.value = saved.email || '';
        this.remember.checked = true;
      }

      saveAuthor() {
        if (!this.remember || !this.nameInput || !this.emailInput) return;
        try {
          if (this.remember.checked) {
            window.localStorage.setItem(
              KvReviewForm.storageKey,
              JSON.stringify({ name: this.nameInput.value, email: this.emailInput.value })
            );
          } else {
            window.localStorage.removeItem(KvReviewForm.storageKey);
          }
        } catch (error) {
          // Storage can be blocked (private mode); the form still sends.
        }
      }
    }
  );
}

// Star rows link to #reviews. On a page with no Reviews tab (the tab is
// switched off, or the product page has no description box) such a link would
// lead nowhere: it becomes plain text, and "Be the first to review" goes.
// Safe to run more than once, as the script can be printed twice on a page.
(() => {
  const checkReviewLinks = () => {
    if (document.querySelector('[id^="KvPdpPanel-reviews-"]')) return;
    document.querySelectorAll('a[data-kv-reviews-open]').forEach((link) => {
      if (link.hasAttribute('data-kv-reviews-first')) link.remove();
      else link.removeAttribute('href');
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', checkReviewLinks);
  else checkReviewLinks();
})();
